import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import JourneyNextTip from '../Common/JourneyNextTip';
import HelpIcon from '../Common/HelpIcon';
import useBookPath from '../../hooks/useBookPath';
import { bookPathTip } from '../../utils/bookPathTip';
import { plotDossiersApi } from '../../api/plotDossiers';
import { plottrackPath } from '../../utils/plottrackPath';
import CastOffer from '../Notes/CastOffer';
import SequenceTalkNote from './SequenceTalkNote';
import './SequencePreviewPanel.css';

export const PREVIEW_EMPTY = 'Chat about this sequence of the story. Scenes will show up here.';
export const PREVIEW_NEEDS_LINE = 'Needs a line';
export const PREVIEW_APPROVE = 'Approve this sequence';
export const PREVIEW_ON_TRACK = 'On the Plot Grid';
export const PREVIEW_OPEN_GRID = 'Open on the Plot Grid';
export const PREVIEW_ALREADY = 'Already on the Plot Grid';
export const PREVIEW_ADD = 'Add a scene';
export const PREVIEW_GENERATE = 'Generate';
export const MIN_WRITER_TURNS = 2;
export const MIN_PARTNER_TURNS = 2;
export const PREVIEW_REMOVE = 'Remove scene';
export const PREVIEW_REPLACE = 'Replace this sequence';
export const PREVIEW_KEEP = "Keep what's on the Plot Grid";
// #1342 — one list the writer owns (design pass #1339, option 1A).
export const PREVIEW_STEPS = ['Chat it through', 'Build your scene list', 'Add to the Plot Grid'];
export const PREVIEW_LIST_LABEL = 'Your scenes for this sequence';
export const PREVIEW_FROM_CHAT = 'From your chat';
export const PREVIEW_KEEP_ROW = 'Keep';
export const PREVIEW_DISMISS_ROW = 'Dismiss';
export const PREVIEW_KEEP_ALL = 'Keep all';
export const PREVIEW_DRAFT_MORE = 'Draft more with AI';
export const PREVIEW_ADD_TO_LIST = 'Add to my list';
export const PREVIEW_REPLACE_LIST = 'Replace my list';
export const PREVIEW_CHOICE_CANCEL = 'Cancel';
export const PREVIEW_CHOICE_LINE = 'You already have scenes here. Add new ones, or replace your list?';
export const PREVIEW_GENERATE_HINT =
  'Drafts a group of scenes into this preview. Cancel spends nothing. This does not put them on the Plot Grid.';
export const PREVIEW_REPLACE_LIST_HINT =
  'This replaces your current list, including your own edits. Cancel spends nothing. This does not put them on the Plot Grid.';
export const PREVIEW_SAVED = 'Saved. Not on the Plot Grid yet.';
export const PREVIEW_EDITED_SINCE = 'Edited since you approved. Approve again to update the Plot Grid.';
export const PREVIEW_NEED_ANY = 'Add a scene or draft some with AI first.';
export const PREVIEW_GO_TO = (n) => `Go to scene ${n}`;
export function previewNeedLine(n, field) {
  return field === 'name'
    ? `Scene ${n} needs a name to approve.`
    : `Scene ${n} needs a "what happens" line to approve.`;
}

function rowBlank(row) {
  return !String(row?.name || '').trim() && !String(row?.what_happens || '').trim();
}

/** #1342: why Approve can't run yet, and which row to fix. Blank rows are ignored. */
export function approveBlocker(scenes) {
  const rows = (scenes || []).map((row, index) => ({ row, n: index + 1 })).filter(({ row }) => !rowBlank(row));
  if (!rows.length) return { reason: PREVIEW_NEED_ANY, rowId: null };
  const bad = rows.find(({ row }) => !String(row.name || '').trim() || !String(row.what_happens || '').trim());
  if (!bad) return null;
  const field = String(bad.row.name || '').trim() ? 'what_happens' : 'name';
  return { reason: previewNeedLine(bad.n, field), rowId: bad.row.id, n: bad.n, field };
}

function sceneKey(row) {
  return (String(row?.name || '').trim() || String(row?.what_happens || '').trim()).toLowerCase();
}
/** #1326: say how close Generate is instead of hiding it silently. */
export function previewGenerateCountdown(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n === 1
    ? 'Generate needs 1 more reply before it can suggest scenes.'
    : `Generate needs ${n} more replies before it can suggest scenes.`;
}

function freshId() {
  return `scene-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function previewState(scenes) {
  const rows = Array.isArray(scenes) ? scenes : [];
  if (!rows.length) return 'empty';
  const ready = rows.every((row) => (
    String(row?.name || '').trim() && String(row?.what_happens || '').trim()
  ));
  return ready ? 'ready' : 'working';
}

function turnCounts(messages) {
  let writer = 0;
  let partner = 0;
  let substantialWriter = 0;
  (messages || []).forEach((message) => {
    const text = String(message?.content || message?.text || '').trim();
    if (!text) return;
    const speaker = String(message?.speaker || message?.role || '').toLowerCase();
    const words = text.split(/\s+/).filter(Boolean).length;
    if (speaker === 'user' || speaker === 'human' || speaker === 'writer') {
      writer += 1;
      if (words >= 4) substantialWriter += 1;
    } else if (speaker === 'assistant' || speaker === 'partner') {
      partner += 1;
    }
  });
  return { writer, partner, substantialWriter };
}

/** Replies still needed before Generate shows (#1326). Same bar as enoughSequenceTalk. */
export function repliesUntilGenerate(messages) {
  const counts = turnCounts(messages);
  return Math.max(
    0,
    MIN_WRITER_TURNS - counts.substantialWriter,
    MIN_PARTNER_TURNS - counts.partner,
  );
}

export function enoughSequenceTalk(messages) {
  const counts = turnCounts(messages);
  return counts.writer >= MIN_WRITER_TURNS
    && counts.partner >= MIN_PARTNER_TURNS
    && counts.substantialWriter >= MIN_WRITER_TURNS;
}

export function sceneCountLabel(count) {
  const n = Number(count) || 0;
  if (n === 1) return '1 scene';
  return `${n} scenes`;
}

export function moveScene(scenes, fromIndex, toIndex) {
  const next = (scenes || []).slice();
  if (toIndex < 0 || toIndex >= next.length || fromIndex === toIndex) return scenes;
  const [row] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, row);
  return next;
}

function rowIncomplete(row) {
  return !String(row?.name || '').trim() || !String(row?.what_happens || '').trim();
}

function dragAllowed() {
  if (typeof document === 'undefined') return true;
  return document.documentElement.getAttribute('data-display-mode') !== 'paperwhite';
}

function PreviewRow({
  row,
  index,
  onChange,
  onMove,
  onRemove,
  onAddBelow,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: !dragAllowed() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : undefined,
  };
  const missing = rowIncomplete(row);

  const onHandleKey = (event) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onMove(index, -1);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onMove(index, 1);
    }
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`seq-preview__row${missing ? ' seq-preview__row--thin' : ''}${isDragging ? ' seq-preview__row--dragging' : ''}`}
    >
      <button
        type="button"
        className="seq-preview__handle"
        aria-label={`Reorder scene ${index + 1}. Arrow up or down.`}
        {...(dragAllowed() ? attributes : {})}
        onPointerDown={dragAllowed() ? listeners?.onPointerDown : undefined}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            onHandleKey(event);
            return;
          }
          if (dragAllowed() && listeners?.onKeyDown) listeners.onKeyDown(event);
        }}
      >
        <span aria-hidden="true">⠿</span>
      </button>
      <div className="seq-preview__body">
        <div className="seq-preview__line">
          <span className="seq-preview__num">{index + 1}</span>
          <label className="seq-preview__field">
            <span className="seq-preview__sr">Scene {index + 1} name</span>
            <textarea
              id={`seq-row-name-${row.id}`}
              className="seq-preview__name"
              rows={2}
              value={row.name}
              placeholder="Scene name"
              onChange={(event) => onChange(row.id, { name: event.target.value })}
            />
          </label>
        </div>
        <label className="seq-preview__field">
          <span className="seq-preview__sr">Scene {index + 1} what happens</span>
          <input
            id={`seq-row-what-${row.id}`}
            className="seq-preview__what"
            value={row.what_happens}
            placeholder="What happens"
            onChange={(event) => onChange(row.id, { what_happens: event.target.value })}
          />
        </label>
        {missing ? (
          <p className="seq-preview__hint">{PREVIEW_NEEDS_LINE}</p>
        ) : null}
        <div className="seq-preview__row-actions">
          <button
            type="button"
            className="seq-preview__text-btn"
            onClick={() => onAddBelow(index)}
          >
            Add below
          </button>
          <button
            type="button"
            className="seq-preview__text-btn"
            onClick={() => onRemove(row.id)}
            aria-label={`${PREVIEW_REMOVE} ${index + 1}`}
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
}

export default function SequencePreviewPanel({
  storyId,
  sessionId,
  sequenceName = '',
  serverPreview = null,
  messages = [],
  api = plotDossiersApi,
}) {
  const [scenes, setScenes] = useState([]);
  const [title, setTitle] = useState(sequenceName || '');
  const [prior, setPrior] = useState([]);
  const [onTrack, setOnTrack] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proseConfirm, setProseConfirm] = useState(false);
  const [proseMessage, setProseMessage] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [wasApproved, setWasApproved] = useState(false);
  const [generateChoice, setGenerateChoice] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const dirtyRef = useRef(false);
  const navigate = useNavigate();
  const saveTimer = useRef(null);

  const state = previewState(scenes);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyView = (view, { scenesToo = true } = {}) => {
    if (!view) return;
    if (view.sequence_name) setTitle(view.sequence_name);
    if (Array.isArray(view.prior_sequences)) setPrior(view.prior_sequences);
    if (Array.isArray(view.suggestions)) {
      setSuggestions(view.suggestions.map((row, index) => ({
        id: String(row.id || `chat-${index + 1}`),
        name: row.name || '',
        what_happens: row.what_happens || '',
      })));
    }
    if (Array.isArray(view.dismissed)) setDismissed(view.dismissed);
    if (view.approved || view.on_plot_track) setWasApproved(true);
    if (scenesToo && Array.isArray(view.scenes)) {
      const nextScenes = view.scenes.map((row, index) => ({
        id: String(row.id || index + 1),
        name: row.name || '',
        what_happens: row.what_happens || '',
      }));
      setScenes(nextScenes);
      if (Object.prototype.hasOwnProperty.call(view, 'on_plot_track')) {
        setOnTrack(!!view.on_plot_track);
      } else {
        setOnTrack(!!view.approved && !view.writer_edited && previewState(nextScenes) === 'ready');
      }
    }
  };

  useEffect(() => {
    setTitle(sequenceName || '');
  }, [sequenceName]);

  useEffect(() => {
    if (!storyId || !sessionId || !api?.sequencePreview) return undefined;
    let cancelled = false;
    dirtyRef.current = false;
    (async () => {
      try {
        const view = await api.sequencePreview(storyId, sessionId);
        if (cancelled || dirtyRef.current) {
          if (!cancelled && view) {
            applyView(view, { scenesToo: false });
          }
          return;
        }
        applyView(view);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load this sequence.');
      }
    })();
    return () => { cancelled = true; };
  }, [storyId, sessionId, api]);

  useEffect(() => {
    if (!serverPreview) return;
    // #1342: chat suggestions always arrive; the writer's rows are never overwritten.
    applyView(serverPreview, { scenesToo: !dirtyRef.current });
  }, [serverPreview]);

  const persist = (next, name, extra = {}) => {
    if (!storyId || !sessionId || !api?.saveSequencePreview) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      Promise.resolve(api.saveSequencePreview(storyId, {
        sessionId,
        sequenceName: name || title,
        scenes: next.filter((row) => !rowBlank(row)),
        ...extra,
      })).catch((err) => {
        setError(err?.message || 'Could not save this sequence.');
      });
    }, 350);
  };

  const updateScenes = (next, extra) => {
    dirtyRef.current = true;
    setOnTrack(false);
    setScenes(next);
    persist(next, title, extra);
  };

  const keepSuggestions = (rows) => {
    const keys = new Set(rows.map(sceneKey));
    const remaining = suggestions.filter((row) => !keys.has(sceneKey(row)));
    setSuggestions(remaining);
    updateScenes(
      [...scenes, ...rows.map((row) => ({ id: freshId(), name: row.name, what_happens: row.what_happens }))],
      { suggestions: remaining },
    );
  };

  const dismissSuggestion = (row) => {
    const remaining = suggestions.filter((item) => sceneKey(item) !== sceneKey(row));
    const nextDismissed = [...dismissed, sceneKey(row)];
    setSuggestions(remaining);
    setDismissed(nextDismissed);
    persist(scenes, title, { suggestions: remaining, dismissed: nextDismissed });
  };

  const goToRow = (blocker) => {
    if (!blocker?.rowId || typeof document === 'undefined') return;
    const field = blocker.field === 'name' ? 'name' : 'what';
    const el = document.getElementById(`seq-row-${field}-${blocker.rowId}`);
    if (el) el.focus();
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = scenes.findIndex((row) => row.id === active.id);
    const to = scenes.findIndex((row) => row.id === over.id);
    if (from < 0 || to < 0) return;
    updateScenes(moveScene(scenes, from, to));
  };

  // After Approve: the next sequence on the suggested path (one at a time).
  const { path: bookPath } = useBookPath(onTrack ? storyId : null, onTrack ? 1 : 0);
  const pathTip = onTrack ? bookPathTip(bookPath, { stage: 'plot', storyId, excludeSequence: title }) : null;
  const onPathStep = async (tip) => {
    if (!tip?.sequence || !storyId || !api?.openWorkshopBeat) return;
    try {
      const opened = await api.openWorkshopBeat(storyId, tip.sequence);
      if (opened?.session_id) navigate(`/chat/${opened.session_id}`);
    } catch (err) {
      setError(err?.message || 'Could not open that sequence.');
    }
  };

  const canGenerate = enoughSequenceTalk(messages);
  const countdown = canGenerate ? '' : previewGenerateCountdown(repliesUntilGenerate(messages));

  const hasRows = scenes.some((row) => !rowBlank(row));

  const onDraft = () => {
    if (hasRows) {
      setGenerateChoice(true);
      return;
    }
    generate('replace');
  };

  const generate = async (mode = 'replace') => {
    setGenerateChoice(false);
    if (!storyId || !sessionId || !api?.generateSequencePreview) {
      setError('Open a book to generate scenes for this sequence.');
      return;
    }
    const replacingList = mode === 'replace' && hasRows;
    const ok = await askPaid('generate_scenes', {
      title: PREVIEW_GENERATE,
      hint: replacingList ? PREVIEW_REPLACE_LIST_HINT : PREVIEW_GENERATE_HINT,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    dirtyRef.current = true;
    try {
      const saved = await api.generateSequencePreview(storyId, {
        sessionId,
        sequenceName: title,
        mode,
      });
      applyView(saved);
    } catch (err) {
      dirtyRef.current = false;
      setError(err?.message || 'Could not draft scenes for this sequence.');
    } finally {
      setBusy(false);
    }
  };

  const approve = async (confirmProse = false) => {
    if (!storyId || !sessionId) {
      setError('Open a book to approve this sequence.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.approveSequencePreview(storyId, {
        sessionId,
        sequenceName: title,
        scenes: scenes.filter((row) => !rowBlank(row)),
        confirmProse,
      });
      dirtyRef.current = false;
      setProseConfirm(false);
      applyView(saved);
    } catch (err) {
      if (err?.status === 409) {
        setProseConfirm(true);
        setProseMessage(err.message || '');
      } else {
        setError(err?.message || 'Could not approve this sequence.');
      }
    } finally {
      setBusy(false);
    }
  };

  const gridPath = plottrackPath(storyId);
  const ids = useMemo(() => scenes.map((row) => row.id), [scenes]);
  const heading = title || 'This sequence';
  const blocker = approveBlocker(scenes);
  const step = onTrack ? 3 : (hasRows || suggestions.length ? 2 : 1);

  return (
    <section className="seq-preview" aria-label="Sequence preview" data-state={state}>
      <header className="seq-preview__head">
        <h2 className="seq-preview__title">{heading}</h2>
        {state !== 'empty' ? (
          <p className="seq-preview__count">{sceneCountLabel(scenes.length)}</p>
        ) : null}
      </header>
      <ol className="seq-preview__steps" aria-label="Steps">
        {PREVIEW_STEPS.map((label, index) => (
          <li
            key={label}
            className={index + 1 === step ? 'is-now' : (index + 1 < step ? 'is-done' : '')}
            aria-current={index + 1 === step ? 'step' : undefined}
          >
            <span aria-hidden="true">{index + 1 < step ? '✓ ' : `${index + 1} · `}</span>
            {label}
            {index === PREVIEW_STEPS.length - 1 ? <HelpIcon hintKey="B3" /> : null}
          </li>
        ))}
      </ol>
      {prior.length > 0 ? (
        <details className="seq-preview__prior">
          <summary>{PREVIEW_ALREADY}</summary>
          <ul>
            {prior.map((item) => (
              <li key={`${item.name}-${item.line}`}>
                <strong>{item.name}</strong>
                {item.line ? <span>{item.line}</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {suggestions.length > 0 ? (
        <section className="seq-preview__suggestions" aria-label={PREVIEW_FROM_CHAT} data-testid="seq-preview-suggestions">
          <div className="seq-preview__suggestions-head">
            <span className="seq-preview__tag">{PREVIEW_FROM_CHAT}</span>
            {suggestions.length > 1 ? (
              <button type="button" className="seq-preview__text-btn" onClick={() => keepSuggestions(suggestions)}>
                {PREVIEW_KEEP_ALL}
              </button>
            ) : null}
          </div>
          <ul>
            {suggestions.map((row) => (
              <li key={row.id} className="seq-preview__suggestion">
                <div>
                  <strong>{row.name || row.what_happens}</strong>
                  {row.name && row.what_happens ? <span>{row.what_happens}</span> : null}
                </div>
                <div className="seq-preview__row-actions">
                  <button
                    type="button"
                    className="seq-preview__text-btn"
                    aria-label={`${PREVIEW_KEEP_ROW} ${row.name || row.what_happens}`}
                    onClick={() => keepSuggestions([row])}
                  >
                    {PREVIEW_KEEP_ROW}
                  </button>
                  <button
                    type="button"
                    className="seq-preview__text-btn"
                    aria-label={`${PREVIEW_DISMISS_ROW} ${row.name || row.what_happens}`}
                    onClick={() => dismissSuggestion(row)}
                  >
                    {PREVIEW_DISMISS_ROW}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {state === 'empty' ? (
        <p className="seq-preview__empty">{PREVIEW_EMPTY}</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ol className="seq-preview__rows">
              {scenes.map((row, index) => (
                <PreviewRow
                  key={row.id}
                  row={row}
                  index={index}
                  onChange={(id, patch) => {
                    updateScenes(scenes.map((item) => (
                      item.id === id ? { ...item, ...patch } : item
                    )));
                  }}
                  onMove={(from, direction) => {
                    updateScenes(moveScene(scenes, from, from + direction));
                  }}
                  onRemove={(id) => {
                    updateScenes(scenes.filter((item) => item.id !== id));
                  }}
                  onAddBelow={(indexTo) => {
                    const next = scenes.slice();
                    next.splice(indexTo + 1, 0, { id: freshId(), name: '', what_happens: '' });
                    updateScenes(next);
                  }}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      )}
      {onTrack ? (
        <p className="seq-preview__on-track" role="status">
          {PREVIEW_ON_TRACK}
          {' '}
          <Link to={gridPath}>{PREVIEW_OPEN_GRID}</Link>
        </p>
      ) : null}
      {/* #1341: first Approve is a good moment to give the book its cast. */}
      {onTrack && storyId ? <CastOffer storyId={storyId} /> : null}
      {pathTip ? <JourneyNextTip tip={pathTip} onAction={onPathStep} /> : null}
      {countdown ? (
        <p className="seq-preview__countdown" role="status" data-testid="seq-preview-countdown">
          {countdown}
        </p>
      ) : null}
      {canGenerate && !onTrack ? (
        <SequenceTalkNote storyId={storyId} sequenceName={title} messages={messages} />
      ) : null}
      <div className="seq-preview__actions">
        <HelpIcon hintKey="S8" />
        <button
          type="button"
          className="seq-preview__text-btn"
          onClick={() => updateScenes([
            ...scenes,
            { id: freshId(), name: '', what_happens: '' },
          ])}
        >
          {PREVIEW_ADD}
        </button>
        {canGenerate ? (
          <button
            type="button"
            className="seq-preview__secondary"
            disabled={busy}
            onClick={onDraft}
          >
            {hasRows ? PREVIEW_DRAFT_MORE : PREVIEW_GENERATE}
          </button>
        ) : null}
        <button
          type="button"
          className="seq-preview__approve"
          disabled={busy || Boolean(blocker)}
          aria-describedby={blocker ? 'seq-preview-why' : undefined}
          onClick={() => approve(false)}
        >
          {PREVIEW_APPROVE}
        </button>
      </div>
      {blocker ? (
        <p id="seq-preview-why" className="seq-preview__why">
          {blocker.reason}
          {blocker.rowId ? (
            <>
              {' '}
              <button type="button" className="seq-preview__text-btn" onClick={() => goToRow(blocker)}>
                {PREVIEW_GO_TO(blocker.n)}
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {generateChoice ? (
        <div className="seq-preview__confirm" role="group" aria-label={PREVIEW_DRAFT_MORE}>
          <p>{PREVIEW_CHOICE_LINE}</p>
          <button type="button" className="seq-preview__approve" onClick={() => generate('add')}>
            {PREVIEW_ADD_TO_LIST}
          </button>
          <button type="button" className="seq-preview__text-btn" onClick={() => generate('replace')}>
            {PREVIEW_REPLACE_LIST}
          </button>
          <button type="button" className="seq-preview__text-btn" onClick={() => setGenerateChoice(false)}>
            {PREVIEW_CHOICE_CANCEL}
          </button>
        </div>
      ) : null}
      {!onTrack && hasRows ? (
        <p className="seq-preview__status" role="status">
          {wasApproved ? PREVIEW_EDITED_SINCE : PREVIEW_SAVED}
        </p>
      ) : null}
      {proseConfirm ? (
        <div className="seq-preview__confirm" role="alertdialog" aria-label={PREVIEW_REPLACE}>
          <p>{proseMessage}</p>
          <button
            type="button"
            className="seq-preview__approve"
            disabled={busy}
            onClick={() => approve(true)}
          >
            {PREVIEW_REPLACE}
          </button>
          <button
            type="button"
            className="seq-preview__text-btn"
            disabled={busy}
            onClick={() => setProseConfirm(false)}
          >
            {PREVIEW_KEEP}
          </button>
        </div>
      ) : null}
      {error ? <p className="seq-preview__error" role="alert">{error}</p> : null}
      {paidDialog}
    </section>
  );
}
